import React, { useEffect, useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
const RestaurantQuickSavingsEstimator = () => {
  // State for slider values
  const [dailyCustomers, setDailyCustomers] = useState(100);
  const [beverageOrderRate, setBeverageOrderRate] = useState(70);
  const [currentAvgPrice, setCurrentAvgPrice] = useState(3.5);
  const [currentCostPerDrink, setCurrentCostPerDrink] = useState(1.2);
  const [smartSodaPrice, setSmartSodaPrice] = useState(4.5);
  // State for calculated results
  const [monthlyResults, setMonthlyResults] = useState({
    currentRevenue: 0,
    currentProfit: 0,
    smartSodaRevenue: 0,
    smartSodaProfit: 0,
    profitIncrease: 0,
    profitMargin: 0,
    bottlesSaved: 0
  });
  // Constants
  const DAYS_PER_MONTH = 30;
  const SMARTSODA_COST_PER_DRINK = 0.45; // 45 cents per drink cost for SmartSoda
  const BASE_MONTHLY_COST = 349; // $349 per month base cost
  const PLASTIC_BOTTLES_PER_MONTH = 0.7; // Percentage of drinks that would have been bottled
  // Calculate results when inputs change
  useEffect(() => {
    // Total monthly drinks ordered
    const monthlyDrinks = dailyCustomers * (beverageOrderRate / 100) * DAYS_PER_MONTH;
    // Current scenario
    const currentRevenue = monthlyDrinks * currentAvgPrice;
    const currentCost = monthlyDrinks * currentCostPerDrink;
    const currentProfit = currentRevenue - currentCost;
    const currentMargin = currentRevenue > 0 ? currentProfit / currentRevenue * 100 : 0;
    // SmartSoda scenario
    const smartSodaRevenue = monthlyDrinks * smartSodaPrice;
    const smartSodaCost = monthlyDrinks * SMARTSODA_COST_PER_DRINK + BASE_MONTHLY_COST;
    const smartSodaProfit = smartSodaRevenue - smartSodaCost;
    const smartSodaMargin = smartSodaRevenue > 0 ? smartSodaProfit / smartSodaRevenue * 100 : 0;
    // Comparison
    const profitIncrease = smartSodaProfit - currentProfit;
    const profitIncreasePercentage = currentProfit > 0 ? profitIncrease / currentProfit * 100 : 0;
    // Environmental impact
    const bottlesSaved = monthlyDrinks * PLASTIC_BOTTLES_PER_MONTH;
    // Update state with safe values
    setMonthlyResults({
      currentRevenue: isFinite(currentRevenue) ? currentRevenue : 0,
      currentProfit: isFinite(currentProfit) ? currentProfit : 0,
      smartSodaRevenue: isFinite(smartSodaRevenue) ? smartSodaRevenue : 0,
      smartSodaProfit: isFinite(smartSodaProfit) ? smartSodaProfit : 0,
      profitIncrease: isFinite(profitIncrease) ? profitIncrease : 0,
      profitMargin: isFinite(smartSodaMargin) ? smartSodaMargin : 0,
      profitIncreasePercentage: isFinite(profitIncreasePercentage) ? profitIncreasePercentage : 0,
      bottlesSaved: isFinite(bottlesSaved) ? bottlesSaved : 0
    });
  }, [dailyCustomers, beverageOrderRate, currentAvgPrice, currentCostPerDrink, smartSodaPrice]);
  // Format currency
  const formatCurrency = value => {
    const safeValue = isFinite(value) ? value : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(safeValue);
  };
  // Format percentage
  const formatPercentage = value => {
    const safeValue = isFinite(value) ? value : 0;
    return `${safeValue.toFixed(1)}%`;
  };
  // Format large numbers
  const formatNumber = value => {
    const safeValue = isFinite(value) ? value : 0;
    return new Intl.NumberFormat('en-US').format(Math.round(safeValue));
  };
  return <section className="py-12 sm:py-16 bg-blue-50" data-id="element-1298">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1299">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden" data-id="element-1300">
          <div className="p-4 sm:p-6 md:p-8" data-id="element-1301">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-navy-950 mb-2 sm:mb-3" data-id="element-1302">
              Restaurant Beverage Profit Calculator
            </h2>
            <p className="text-center text-gray-600 text-sm sm:text-base mb-6 sm:mb-8 max-w-2xl mx-auto" data-id="element-1303">
              See how SmartSoda can transform your restaurant's beverage profit
              margins. Adjust the parameters below based on your business.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-6 sm:mb-8" data-id="element-1304">
              {/* Daily Customers */}
              <div className="space-y-1" data-id="element-1305">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-1306">
                  <label htmlFor="dailyCustomers" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-1307">
                    Average Daily Customers
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-1308">
                    {dailyCustomers} customers
                  </span>
                </div>
                <input type="range" id="dailyCustomers" min="25" max="500" step="25" value={dailyCustomers} onChange={e => setDailyCustomers(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1309" />
                <div className="flex justify-between mt-0.5" data-id="element-1310">
                  <span className="text-xs text-gray-500" data-id="element-1311">25</span>
                  <span className="text-xs text-gray-500" data-id="element-1312">500</span>
                </div>
              </div>
              {/* Beverage Order Rate */}
              <div className="space-y-1" data-id="element-1313">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-1314">
                  <label htmlFor="beverageOrderRate" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-1315">
                    % of Customers Ordering Beverages
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-1316">
                    {beverageOrderRate}%
                  </span>
                </div>
                <input type="range" id="beverageOrderRate" min="40" max="95" step="5" value={beverageOrderRate} onChange={e => setBeverageOrderRate(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1317" />
                <div className="flex justify-between mt-0.5" data-id="element-1318">
                  <span className="text-xs text-gray-500" data-id="element-1319">40%</span>
                  <span className="text-xs text-gray-500" data-id="element-1320">95%</span>
                </div>
              </div>
              {/* Current Average Beverage Price */}
              <div className="space-y-1" data-id="element-1321">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-1322">
                  <label htmlFor="currentAvgPrice" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-1323">
                    Current Avg. Beverage Price ($)
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-1324">
                    ${currentAvgPrice.toFixed(2)}
                  </span>
                </div>
                <input type="range" id="currentAvgPrice" min="2" max="7" step="0.25" value={currentAvgPrice} onChange={e => setCurrentAvgPrice(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1325" />
                <div className="flex justify-between mt-0.5" data-id="element-1326">
                  <span className="text-xs text-gray-500" data-id="element-1327">$2.00</span>
                  <span className="text-xs text-gray-500" data-id="element-1328">$7.00</span>
                </div>
              </div>
              {/* Current Cost Per Beverage */}
              <div className="space-y-1" data-id="element-1329">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-1330">
                  <label htmlFor="currentCostPerDrink" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-1331">
                    Current Cost Per Beverage ($)
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-1332">
                    ${currentCostPerDrink.toFixed(2)}
                  </span>
                </div>
                <input type="range" id="currentCostPerDrink" min="0.5" max="3" step="0.1" value={currentCostPerDrink} onChange={e => setCurrentCostPerDrink(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1333" />
                <div className="flex justify-between mt-0.5" data-id="element-1334">
                  <span className="text-xs text-gray-500" data-id="element-1335">$0.50</span>
                  <span className="text-xs text-gray-500" data-id="element-1336">$3.00</span>
                </div>
              </div>
              {/* SmartSoda Price */}
              <div className="space-y-1 md:col-span-2" data-id="element-1337">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-1338">
                  <label htmlFor="smartSodaPrice" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-1339">
                    SmartSoda Menu Price ($)
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-1340">
                    ${smartSodaPrice.toFixed(2)}
                  </span>
                </div>
                <input type="range" id="smartSodaPrice" min="3" max="8" step="0.25" value={smartSodaPrice} onChange={e => setSmartSodaPrice(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1341" />
                <div className="flex justify-between mt-0.5" data-id="element-1342">
                  <span className="text-xs text-gray-500" data-id="element-1343">$3.00</span>
                  <span className="text-xs text-gray-500" data-id="element-1344">$8.00</span>
                </div>
              </div>
            </div>
            {/* Results Section */}
            <div className="bg-blue-50 p-4 sm:p-6 rounded-lg sm:rounded-xl mb-6 sm:mb-8" data-id="element-1345">
              <h3 className="text-lg font-semibold text-center text-navy-950 mb-4" data-id="element-1346">
                Monthly Profit Analysis
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-5" data-id="element-1347">
                <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-1348">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1349">
                    Current System
                  </h4>
                  <p className="text-lg font-semibold" data-id="element-1350">
                    Revenue:{' '}
                    <span className="text-gray-700" data-id="element-1351">
                      {formatCurrency(monthlyResults.currentRevenue)}
                    </span>
                  </p>
                  <p className="text-lg font-semibold" data-id="element-1352">
                    Profit:{' '}
                    <span className="text-gray-700" data-id="element-1353">
                      {formatCurrency(monthlyResults.currentProfit)}
                    </span>
                  </p>
                  <p className="text-lg font-semibold" data-id="element-1354">
                    Margin:{' '}
                    <span className="text-gray-700" data-id="element-1355">
                      {formatPercentage(monthlyResults.currentProfit / monthlyResults.currentRevenue * 100)}
                    </span>
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-1356">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1357">
                    With SmartSoda
                  </h4>
                  <p className="text-lg font-semibold" data-id="element-1358">
                    Revenue:{' '}
                    <span className="text-blue-600" data-id="element-1359">
                      {formatCurrency(monthlyResults.smartSodaRevenue)}
                    </span>
                  </p>
                  <p className="text-lg font-semibold" data-id="element-1360">
                    Profit:{' '}
                    <span className="text-blue-600" data-id="element-1361">
                      {formatCurrency(monthlyResults.smartSodaProfit)}
                    </span>
                  </p>
                  <p className="text-lg font-semibold" data-id="element-1362">
                    Margin:{' '}
                    <span className="text-blue-600" data-id="element-1363">
                      {formatPercentage(monthlyResults.profitMargin)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-1364">
                <div className="text-center bg-green-50 p-4 rounded-lg shadow-sm" data-id="element-1365">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1366">
                    Monthly Profit Increase
                  </h3>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600" data-id="element-1367">
                    {formatCurrency(monthlyResults.profitIncrease)}
                  </p>
                  <p className="text-sm text-green-600 mt-1" data-id="element-1368">
                    {formatPercentage(monthlyResults.profitIncreasePercentage)}{' '}
                    increase
                  </p>
                </div>
                <div className="text-center bg-blue-50 p-4 rounded-lg shadow-sm" data-id="element-1369">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1370">
                    Environmental Impact
                  </h3>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600" data-id="element-1371">
                    {formatNumber(monthlyResults.bottlesSaved)}
                  </p>
                  <p className="text-sm text-blue-600 mt-1" data-id="element-1372">
                    Plastic bottles eliminated monthly
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1373">
                *Estimates are based on industry averages and may vary based on
                your specific restaurant operations.
              </p>
            </div>
            {/* CTA Button */}
            <div className="text-center" data-id="element-1374">
              <a href="#book-demo" className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-1375">
                Get Your Custom Restaurant Proposal
                <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" data-id="element-1376" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default RestaurantQuickSavingsEstimator;